import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import Reveal from "@/components/Reveal";
import Eyebrow from "@/components/Eyebrow";
import { IMG } from "@/lib/images";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SERVICES = [
  "Website Design",
  "Hosting & Maintenance",
  "Social Media Marketing",
  "Multiple / Not sure",
];

const initialForm = {
  name: "",
  email: "",
  company: "",
  service_interest: "",
  message: "",
  website: "", // honeypot
};

export default function Contact() {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    document.title = "Contact — Bishop Creative";
  }, []);

  const update = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
  };

  const validate = () => {
    if (!form.name.trim()) return "Please share your name.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()))
      return "Please enter a valid email address.";
    if (form.message.trim().length < 10)
      return "Tell us a little more — at least a sentence or two.";
    return "";
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    const v = validate();
    if (v) {
      setError(v);
      toast.error(v);
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        company: form.company.trim() || null,
        service_interest: form.service_interest || null,
        message: form.message.trim(),
        website: form.website,
      });
      if (res.data?.success) {
        setSuccess(true);
        setForm(initialForm);
        toast.success("Message sent. We’ll be in touch within 1–2 business days.");
      } else {
        throw new Error(res.data?.message || "Unexpected response.");
      }
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.message ||
        "Something went wrong. Please try again.";
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      {/* HERO */}
      <section className="relative pt-28 md:pt-40 pb-16 md:pb-24">
        <div className="container-page grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end">
          <div className="lg:col-span-7">
            <Reveal>
              <Eyebrow>Contact</Eyebrow>
            </Reveal>
            <Reveal delay={0.08}>
              <h1 className="mt-8 font-display text-5xl md:text-7xl lg:text-[88px] leading-[0.95] tracking-[-0.02em] max-w-[18ch]">
                Let's talk about your business.
              </h1>
            </Reveal>
            <Reveal delay={0.16}>
              <p className="mt-8 max-w-[56ch] text-[color:var(--text-2)] text-lg leading-[1.8]">
                Share a little about your brand, your timeline, and what you’re
                hoping to make. We reply personally within 24 hours.
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={0.2}>
              <div className="relative aspect-[4/5] overflow-hidden">
                <img
                  src={IMG.heroContact}
                  alt="Coastal fog rolling off the California coast"
                  className="absolute inset-0 h-full w-full object-cover"
                />
                <div
                  aria-hidden
                  className="absolute inset-0"
                  style={{
                    background:
                      "linear-gradient(180deg, rgba(11,10,8,0.15) 0%, rgba(11,10,8,0.55) 100%)",
                  }}
                />
                <div className="noise-overlay" />
                <figcaption className="absolute left-4 bottom-4 right-4 flex items-center justify-between">
                  <span className="eyebrow text-[color:var(--text-2)]">
                    Central Coast · Fog at dusk
                  </span>
                  <span className="eyebrow text-[color:var(--text-3)]">Plate 03</span>
                </figcaption>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <div className="container-page">
        <div className="hairline-strong" />
      </div>

      {/* FORM */}
      <section className="section-pad">
        <div className="container-page grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-20">
          <aside className="lg:col-span-4">
            <Reveal>
              <Eyebrow>Details</Eyebrow>
            </Reveal>
            <Reveal delay={0.06}>
              <h2 className="mt-6 font-display text-3xl md:text-4xl leading-[1.1]">
                Get in touch.
              </h2>
            </Reveal>

            <div className="mt-10 space-y-8">
              <div>
                <div className="eyebrow">Correspondence</div>
                <div className="mt-2 text-[color:var(--text-2)]">
                  hello@bishopcreative.co
                </div>
              </div>
              <div className="hairline" />
              <div>
                <div className="eyebrow">Studio</div>
                <div className="mt-2 text-[color:var(--text-2)] leading-[1.7]">
                  San Luis Obispo, California
                  <br />
                  <span className="text-[color:var(--text-3)] text-sm">
                    Serving Paso Robles, Santa Barbara & the Central Coast
                  </span>
                </div>
              </div>
               
            </div>
          </aside>

          <div className="lg:col-span-8">
            {success ? (
              <Reveal>
                <div
                  className="p-8 md:p-12 border border-[color:rgba(198,168,106,0.35)]"
                  style={{ background: "rgba(20,17,14,0.6)" }}
                  data-testid="contact-form-success-message"
                >
                  <div className="eyebrow" style={{ color: "var(--brass)" }}>
                    Message received
                  </div>
                  <h3 className="mt-6 font-display text-3xl md:text-4xl leading-[1.1]">
                    Thank you — we’ll write back shortly.
                  </h3>
                  <p className="mt-6 max-w-[56ch] text-[color:var(--text-2)] leading-[1.8]">
                    Your note has arrived at the studio. Expect a personal reply
                    from one of our directors shortly.
                    In the meantime, you’re welcome to browse our services or
                    read more about the studio.
                  </p>
                  <div className="mt-10 flex flex-wrap gap-4">
                    <button
                      type="button"
                      onClick={() => setSuccess(false)}
                      className="btn btn-ghost"
                      data-testid="contact-form-reset"
                    >
                      Send another
                    </button>
                  </div>
                </div>
              </Reveal>
            ) : (
              <form
                onSubmit={onSubmit}
                noValidate
                className="w-full"
                data-testid="contact-form"
              >
                {/* Honeypot */}
                <div className="hidden" aria-hidden="true">
                  <label htmlFor="website">Website</label>
                  <input
                    id="website"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    value={form.website}
                    onChange={update("website")}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-12">
                  <Reveal>
                    <div>
                      <label className="luxe-label" htmlFor="name">
                        Name
                      </label>
                      <input
                        id="name"
                        type="text"
                        className="luxe-input mt-3"
                        placeholder="Your full name"
                        value={form.name}
                        onChange={update("name")}
                        required
                        data-testid="contact-form-name-input"
                      />
                    </div>
                  </Reveal>
                  <Reveal delay={0.06}>
                    <div>
                      <label className="luxe-label" htmlFor="email">
                        Email
                      </label>
                      <input
                        id="email"
                        type="email"
                        className="luxe-input mt-3"
                        placeholder="you@domain.com"
                        value={form.email}
                        onChange={update("email")}
                        required
                        data-testid="contact-form-email-input"
                      />
                    </div>
                  </Reveal>
                  <Reveal delay={0.1}>
                    <div>
                      <label className="luxe-label" htmlFor="company">
                        Company
                      </label>
                      <input
                        id="company"
                        type="text"
                        className="luxe-input mt-3"
                        placeholder="Business name"
                        value={form.company}
                        onChange={update("company")}
                        data-testid="contact-form-company-input"
                      />
                    </div>
                  </Reveal>
                  <Reveal delay={0.14}>
                    <div>
                      <label className="luxe-label" htmlFor="service">
                        Service interest
                      </label>
                      <div className="relative mt-3">
                        <select
                          id="service"
                          className="luxe-input appearance-none pr-8"
                          value={form.service_interest}
                          onChange={update("service_interest")}
                          data-testid="contact-form-service-select"
                          style={{ backgroundColor: "transparent" }}
                        >
                          <option value="" style={{ background: "#14110E" }}>
                            Select a service
                          </option>
                          {SERVICES.map((s) => (
                            <option
                              key={s}
                              value={s}
                              style={{ background: "#14110E" }}
                            >
                              {s}
                            </option>
                          ))}
                        </select>
                        <span
                          aria-hidden
                          className="pointer-events-none absolute right-1 top-1/2 -translate-y-1/2 text-[color:var(--text-3)] text-xs"
                        >
                          ⌄
                        </span>
                      </div>
                    </div>
                  </Reveal>
                </div>

                <Reveal delay={0.18}>
                  <div className="mt-10">
                    <label className="luxe-label" htmlFor="message">
                      Project
                    </label>
                    <textarea
                      id="message"
                      rows={6}
                      className="luxe-input mt-3 resize-none"
                      placeholder="Tell us about the business, the ambition, and anything that would help us understand the work."
                      value={form.message}
                      onChange={update("message")}
                      required
                      data-testid="contact-form-message-textarea"
                    />
                  </div>
                </Reveal>

                {error && (
                  <div
                    className="mt-8 text-sm"
                    style={{ color: "#D97B68" }}
                    data-testid="contact-form-error-message"
                  >
                    {error}
                  </div>
                )}

                <Reveal delay={0.24}>
                  <div className="mt-12 flex flex-col md:flex-row md:items-center gap-6">
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={submitting}
                      data-testid="contact-form-submit-button"
                    >
                      {submitting ? (
                        <>
                          <Loader2 size={16} className="animate-spin" />
                          Sending…
                        </>
                      ) : (
                        "Send Inquiry"
                      )}
                    </button>
                    <span className="text-xs text-[color:var(--text-3)] max-w-[40ch]">
                      We reply within 24 hours. Your details stay with
                      the studio — never shared.
                    </span>
                  </div>
                </Reveal>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
